import { cva } from 'class-variance-authority';
import { formatAmount } from '@/shared/lib';
import { useTranslation } from 'react-i18next';
import { NUMERIC_CONFIG } from '@/shared/config';
import { cn } from '@/shared/utils';
import TriangleArrowIcon from '@/shared/assets/triangle-arrow-icon.svg?react';

const deltaLabelVariants = cva(
  'rounded-full px-[1.2rem] py-[0.4rem] flex items-center',
  {
    variants: {
      size: {
        default: 'headline5',
        small: 'medium5',
      },
      deltaDirectionType: {
        arrow: 'gap-[0.4rem]',
        mathSign: '',
      },
      direction: {
        positive: 'bg-cyan/10 text-cyan',
        negative: 'bg-light-red text-red',
      },
    },
    defaultVariants: {
      size: 'default',
      direction: 'positive',
    },
  }
);

const getDirectionClassName = (
  isPositive: boolean,
  positiveClassName?: string,
  negativeClassName?: string
): string => {
  if (isPositive && positiveClassName) return positiveClassName;
  if (!isPositive && negativeClassName) return negativeClassName;
  return '';
};

type DeltaDirectionType = 'arrow' | 'mathSign';

type Props = {
  deltaPercent: number | null | undefined;
  deltaDirectionType?: DeltaDirectionType;
  showLabelText?: boolean;
  size?: 'default' | 'small';
  className?: string;
  label?: string;
  arrowPositiveClassName?: string;
  arrowNegativeClassName?: string;
  deltaPositiveClassName?: string;
  deltaNegativeClassName?: string;
};

const DeltaLabel = ({
  deltaPercent,
  deltaDirectionType = 'arrow',
  size = 'default',
  showLabelText = true,
  className,
  label,
  deltaPositiveClassName,
  deltaNegativeClassName,
  arrowPositiveClassName,
  arrowNegativeClassName,
}: Props) => {
  const { t } = useTranslation();

  if (deltaPercent == null) {
    return null;
  }

  const roundedDeltaPercentString = formatAmount(
    Math.abs(deltaPercent),
    NUMERIC_CONFIG.precision.default
  );

  const deltaIsPositive = deltaPercent >= 0;
  const directionSign =
    deltaDirectionType === 'arrow' ? (
      <TriangleArrowIcon
        className={cn(
          deltaIsPositive ? 'text-cyan' : 'text-red',
          !deltaIsPositive && 'rotate-180',
          deltaIsPositive && arrowPositiveClassName,
          !deltaIsPositive && arrowNegativeClassName
        )}
      />
    ) : deltaIsPositive ? (
      '+'
    ) : (
      '-'
    );

  const direction = deltaIsPositive ? 'positive' : 'negative';

  return (
    <div
      className={cn(
        deltaLabelVariants({ size, deltaDirectionType, direction }),
        getDirectionClassName(
          deltaIsPositive,
          deltaPositiveClassName,
          deltaNegativeClassName
        ),
        className
      )}>
      <span>{directionSign}</span>
      {roundedDeltaPercentString}%
      {showLabelText && (
        <span className="hidden md:block ml-[0.4rem]">
          {label || t('common.today')}
        </span>
      )}
    </div>
  );
};

export { DeltaLabel };
